import * as React from "react";

import { Button, HTMLTable, NumericInput } from "@blueprintjs/core";
import { produce } from "immer";

import { Grade, Juror, Jurors, Participant, Participants, Stage } from "../Interfaces";

interface State {
	grades: Grade[];
}

function makeGrade(juror: Juror, participant: Participant, stage: Stage): Grade {
	let repopprev = 0;
	if (participant.id === stage.reporter_id) {
		repopprev = 1;
	} else if (participant.id === stage.opponent_id) {
		repopprev = 2;
	} else if (participant.id === stage.reviewer_id) {
		repopprev = 3;
	}
	return {
		juror_id: juror.id,
		participant_id: participant.id,
		repopprev,
		grade: 1,
		grade_counts: true,
	};
}

function chain<A, B>(f: (a: A) => B[], l: A[]): B[] {
	return l.map(f).reduce((a, c) => a.concat(c), []);
}

function cartesian<A, B, C>(f: (a: A, b: B) => C, as: A[], bs: B[]): C[] {
	return chain((a) => bs.map((b) => f(a, b)), as);
}

function makeGrades(jurors: Jurors, participants: Participants, stage: Stage) {
	return cartesian(
		(j, p) => makeGrade(j, p, stage),
		Object.values(jurors),
		Object.values(participants)
	);
}

export default class GradeTable extends React.Component<
	{
		jurors: Jurors;
		participants: Participants;
		stage: Stage;
		onGradeSubmission: (stageId: number, grades: Grade[]) => void;
	},
	State
> {
	public state: State = {
		grades: makeGrades(this.props.jurors, this.props.participants, this.props.stage).filter(
			(grade) => grade.repopprev
		),
	};

	private onChangeGrade(
		participant_id: number,
		juror_id: number,
		repopprev: number,
		value: number
	) {
		this.setState(
			produce((state) => {
				const index = this.state.grades.findIndex(
					(a) => a.participant_id === participant_id && a.juror_id === juror_id
				);
				const gradeValue = value ? Math.floor(value) : undefined;
				const grade = {
					participant_id,
					juror_id,
					repopprev,
					grade: gradeValue,
				};
				state.grades[index] = grade;
			})
		);
	}

	private makeRow = (
		label: string,
		participant: Participant,
		jurors: Jurors,
		repopprev: number
	) => (
		<tr>
			<td>{label}</td>
			<td>{participant && participant.firstname + " " + participant.lastname}</td>
			{Object.values(jurors)
				.sort((a, b) => a.position - b.position)
				.map((juror) => (
					<td key={juror.id}>
						<NumericInput
							onValueChange={(value) => this.onChangeGrade(participant.id, juror.id, 2, value)}
							max={10}
							min={1}
							majorStepSize={repopprev}
							selectAllOnFocus={true}
							clampValueOnBlur={true}
							value={
								this.state.grades.find(
									(grade) => grade.participant_id === participant.id && grade.juror_id === juror.id
								)!.grade
							}
							buttonPosition={"none"}
						/>
					</td>
				))}
		</tr>
	);
	public render() {
		const allParticipants: Participant[] = [];
		Object.keys(this.props.participants)
			.map((key) => this.props.participants[key])
			.forEach((participant) => {
				allParticipants.push(participant);
			});
		const reporter = allParticipants.find(
			(participant) => participant.id === this.props.stage.reporter_id
		);
		const opponent = allParticipants.find(
			(participant) => participant.id === this.props.stage.opponent_id
		);
		const reviewer = allParticipants.find(
			(participant) => participant.id === this.props.stage.reviewer_id
		);
		return (
			<div>
				<HTMLTable striped={true}>
					<thead>
						<tr>
							<th key={-20} />
							<th key={-10} />
							{Object.keys(this.props.jurors)
								.map((key) => this.props.jurors[key])
								.sort((a, b) => a.position - b.position)
								.map((juror) => (
									<th key={juror.lastname}>
										{juror.firstname} {juror.lastname}
									</th>
								))}
						</tr>
					</thead>
					<tbody>
						{this.makeRow("Reporter", reporter!, this.props.jurors, 1)}
						{this.makeRow("Opponent", opponent!, this.props.jurors, 2)}
						{this.makeRow("Reviewer", reviewer!, this.props.jurors, 3)}
					</tbody>
				</HTMLTable>
				<Button
					onClick={(e: any) => this.props.onGradeSubmission(this.props.stage.id, this.state.grades)}
					text={" Submit Grades! "}
				/>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/FightRoom/GradeTable.tsx